import React, { useState, useEffect, useCallback } from "react";
import Sidebar from "../components/Sidebar";
import CustomToolbar from "../components/CustomToolbar";
import Pagination from "../components/Pagination";
import LoadingSpinner from "../components/LoadingSpinner";
import "../styles/components.css";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import Swal from "sweetalert2";
import axios from "axios";
import { DataGrid, useGridApiRef } from "@mui/x-data-grid";
import CheckIcon from "@mui/icons-material/Check";
import TextField from "@mui/material/TextField";
import { format, parseISO, isValid } from "date-fns";

function CreditUnblockDateEditor() {
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [filters, setFilters] = useState({});
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [pageSize] = useState(10);
    const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
    const [filterAnchor, setFilterAnchor] = useState(null);
    const [filtersInitialized, setFiltersInitialized] = useState(false);
    const [savingRows, setSavingRows] = useState({});
    const [editingRowId, setEditingRowId] = useState(null);
    const [originalDates, setOriginalDates] = useState({});

    const { t, i18n } = useTranslation();
    const { user, token } = useAuth();
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    const gridApiRef = useGridApiRef();

    useEffect(() => {
        const savedFilters = localStorage.getItem("creditUnblockDateFilters");
        if (savedFilters) {
            try {
                const parsed = JSON.parse(savedFilters);
                if (parsed.filters) setFilters(parsed.filters);
                if (parsed.searchQuery) setSearchQuery(parsed.searchQuery);
            } catch (error) {
                console.error("Error parsing saved filters:", error);
            }
        }
        setFiltersInitialized(true);
    }, []);

    useEffect(() => {
        if (!filtersInitialized) return;

        const filtersToSave = {
            filters,
            searchQuery,
        };

        localStorage.setItem(
            "creditUnblockDateFilters",
            JSON.stringify(filtersToSave)
        );
    }, [filters, searchQuery, filtersInitialized]);

    const getDateInputValue = (dateValue) => {
        if (!dateValue) return "";

        try {
            const parsedDate =
                typeof dateValue === "string" ? parseISO(dateValue) : new Date(dateValue);

            if (!isValid(parsedDate)) return "";

            return format(parsedDate, "yyyy-MM-dd");
        } catch (error) {
            return "";
        }
    };

    const fetchCustomers = useCallback(
        async (pageNumber = 1, searchTerm = "", customFilters = {}) => {
            setLoading(true);
            try {
                const apiUrl = `${API_BASE_URL}/customers/pagination`;

                const params = new URLSearchParams({
                    page: pageNumber,
                    pageSize: pageSize,
                    search: searchTerm,
                    sortBy: "id",
                    sortOrder: "asc",
                    filters: JSON.stringify(customFilters || {}),
                });

                const response = await axios.get(`${apiUrl}?${params}`, {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                });

                if (response.data?.status?.toLowerCase() === "ok") {
                    const rows = (response.data?.data?.data || []).map((customer) => ({
                        id: customer.id,
                        serialNumber: customer.id,
                        erpCustId: customer.erpCustId || "",
                        customerName:
                            i18n.language === "ar"
                                ? customer.companyNameAr || customer.companyNameEn || ""
                                : customer.companyNameEn || customer.companyNameAr || "",
                        status: customer.isBlocked ? "Blocked" : "Active",
                        isBlocked: customer.isBlocked,
                        unblockedUntil: customer.unblockedUntil || "",
                        rawData: customer,
                    }));

                    setCustomers(rows);

                    const datesMap = {};
                    rows.forEach((row) => {
                        datesMap[row.id] = row.unblockedUntil || "";
                    });
                    setOriginalDates(datesMap);

                    setTotalPages(response.data?.data?.totalPages || 1);
                    setTotal(response.data?.data?.totalRecords || 0);
                }
            } catch (err) {
                console.error("Error fetching customers:", err);
                Swal.fire({
                    title: t("Error"),
                    text: err.response?.data?.message || t("Failed to fetch customers"),
                    icon: "error",
                    confirmButtonText: t("OK"),
                });
            } finally {
                setLoading(false);
            }
        },
        [API_BASE_URL, pageSize, token, t, i18n.language]
    );

    useEffect(() => {
        if (!filtersInitialized || !user) return;
        fetchCustomers(page, searchQuery, filters);
    }, [page, searchQuery, filters, user, filtersInitialized, fetchCustomers]);

    const handleSearch = (searchTerm) => {
        setSearchQuery(searchTerm);
        setPage(1);
    };

    const handleFilterChange = (newFilters) => {
        setFilters(newFilters);
        setPage(1);
        setFilterAnchor(null);
    };

    const handleColumnVisibilityChange = (newModel) => {
        setColumnVisibilityModel(newModel);
    };

    const handleDateChange = (rowId, value) => {
        setCustomers((prev) =>
            prev.map((row) =>
                row.id === rowId ? { ...row, unblockedUntil: value } : row
            )
        );
    };

    const handleDateFocus = (row) => {
        setEditingRowId(row.id);
        setOriginalDates((prev) => ({
            ...prev,
            [row.id]: row.unblockedUntil || "",
        }));
    };

    const handleSaveOrUpdate = async (row) => {
        if (!row.unblockedUntil) {
            Swal.fire({
                title: t("Validation Error"),
                text: t("Please select Credit Unblocked Until date"),
                icon: "warning",
                confirmButtonText: t("OK"),
            });
            return;
        }

        setSavingRows((prev) => ({ ...prev, [row.id]: true }));

        try {
            const apiUrl = `${API_BASE_URL}/customers/unblock-credit/${row.id}`;

            const payload = {
                id: row.id,
                unblockedUntil: row.unblockedUntil,
            };

            const response = await axios.post(apiUrl, payload, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });

            if (response.data?.status?.toLowerCase() === "ok") {
                setOriginalDates((prev) => ({
                    ...prev,
                    [row.id]: row.unblockedUntil,
                }));
                setEditingRowId(null);

                Swal.fire({
                    title: t("Success"),
                    text: t("Credit unblock date saved successfully"),
                    icon: "success",
                    showConfirmButton: false,
                    timer: 1000,
                });

                fetchCustomers(page, searchQuery, filters);
            }
        } catch (err) {
            console.error("Error saving credit unblock date:", err);
            Swal.fire({
                title: t("Error"),
                text:
                    err.response?.data?.message ||
                    t("Failed to save credit unblock date"),
                icon: "error",
                confirmButtonText: t("OK"),
            });
        } finally {
            setSavingRows((prev) => ({ ...prev, [row.id]: false }));
        }
    };

    const columns = [
        {
            field: "serialNumber",
            headerName: t("ID"),
            width: 90,
            searchable: false,
            include: true,
            align: "left",
            headerAlign: "center",
        },
        {
            field: "erpCustId",
            headerName: t("ERP Customer ID"),
            flex: 1,
            minWidth: 160,
            searchable: true,
            include: true,
            align: "left",
            headerAlign: "center",
        },
        {
            field: "customerName",
            headerName: t("Customer Name"),
            flex: 1.5,
            minWidth: 220,
            searchable: true,
            include: true,
            align: "left",
            headerAlign: "center",
        },
        {
            field: "status",
            headerName: t("Status"),
            flex: 1,
            minWidth: 130,
            searchable: true,
            include: true,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => {
                const isBlocked = params.row.isBlocked;

                return (
                    <span
                        style={{
                            padding: "4px 10px",
                            fontSize: "12px",
                            fontWeight: 600,
                            color: isBlocked ? "#b42318" : "#027a48",
                        }}
                    >
                        {t(isBlocked ? "Blocked" : "Active")}
                    </span>
                );
            },
        },
        {
            field: "unblockedUntil",
            headerName: t("Credit Unblocked Until"),
            flex: 1.7,
            minWidth: 300,
            sortable: false,
            searchable: false,
            include: true,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => {
                const row = params.row;
                const dateInputValue = getDateInputValue(row.unblockedUntil);
                const isSaving = savingRows[row.id] || false;
                const isEditing = editingRowId === row.id;
                const originalDate = getDateInputValue(originalDates[row.id]);
                const hasDateChanged = dateInputValue !== originalDate;

                return (
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "8px",
                            width: "100%",
                        }}
                    >
                        <TextField
                            type="date"
                            size="small"
                            value={dateInputValue}
                            onFocus={() => handleDateFocus(row)}
                            onChange={(e) => handleDateChange(row.id, e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            InputLabelProps={{ shrink: true }}
                            sx={{
                                minWidth: 170,
                                "& .MuiInputBase-root": {
                                    height: "36px",
                                    display: "flex",
                                    alignItems: "center",
                                },
                                "& .MuiInputBase-input": {
                                    padding: "8.5px 10px",
                                    fontSize: "13px",
                                    textAlign: "center",
                                },
                            }}
                        />

                        {isEditing ? (
                            <button
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleSaveOrUpdate(row);
                                }}
                                disabled={isSaving || !hasDateChanged}
                                style={{
                                    padding: "6px 8px",
                                    backgroundColor: "transparent",
                                    color: hasDateChanged ? "#027a48" : "#98a2b3",
                                    cursor:
                                        isSaving || !hasDateChanged ? "not-allowed" : "pointer",
                                    display: "flex",
                                    border: "none",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    opacity: isSaving || !hasDateChanged ? 0.7 : 1,
                                }}
                                title={t("Save")}
                            >
                                <CheckIcon fontSize="small" />
                            </button>
                        ) : null}
                    </div>
                );
            },
        },
    ];

    const visibleColumns = columns.filter((col) => col.include !== false);
    const searchableFields = visibleColumns
        .filter((item) => item.searchable)
        .map((item) => item.field);
    const filteredData = visibleColumns.filter((item) =>
        searchableFields.includes(item.field)
    );

    const columnsToDisplay = {
        erpCustId: "ERP Customer ID",
        customerName: "Customer Name",
        status: "Status",
    };

    return (
        <Sidebar title={t("Credit Unblock Date Editor")}>
            <div className="orders-content">
                {loading && <LoadingSpinner />}

                <div className="table-container">
                    <div
                        style={{
                            width: "100%",
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        <DataGrid
                            apiRef={gridApiRef}
                            rows={customers}
                            columns={visibleColumns}
                            pageSize={pageSize}
                            rowCount={total}
                            disableRowSelectionOnClick
                            disableColumnMenu
                            hideFooter={true}
                            hideFooterPagination={true}
                            paginationMode="server"
                            rowHeight={62}
                            showToolbar
                            columnVisibilityModel={columnVisibilityModel}
                            onColumnVisibilityModelChange={handleColumnVisibilityChange}
                            slots={{
                                toolbar: () => (
                                    <CustomToolbar
                                        searchQuery={searchQuery}
                                        filterAnchor={filterAnchor}
                                        onSearch={handleSearch}
                                        setSearchQuery={setSearchQuery}
                                        setFilterAnchor={setFilterAnchor}
                                        handleFilterChange={handleFilterChange}
                                        onColumnVisibilityChange={setColumnVisibilityModel}
                                        columns={filteredData}
                                        filters={filters}
                                        columnVisibilityModel={columnVisibilityModel}
                                        searchPlaceholder={t("Search customers...")}
                                        showColumnVisibility={false}
                                        showFilters={false}
                                        showCalendar={false}
                                        showExport={false}
                                        showUpload={false}
                                        showAdd={false}
                                        showApproval={false}
                                        columnsToDisplay={columnsToDisplay}
                                        excludeFiltersFromChips={[]}
                                    />
                                ),
                            }}
                            sx={{
                                flex: 1,
                                display: "flex",
                                flexDirection: "column",
                                "& .MuiDataGrid-toolbar": {
                                    padding: "0px 8px !important",
                                    minHeight: "56px !important",
                                    flexShrink: 0,
                                },
                                "& .MuiDataGrid-main": {
                                    flex: 1,
                                    overflow: "hidden",
                                    display: "flex",
                                    flexDirection: "column",
                                },
                                "& .MuiDataGrid-virtualScroller": {
                                    overflow: "auto !important",
                                    flex: 1,
                                },
                                "& .MuiDataGrid-columnHeaders": {
                                    top: 0,
                                    zIndex: 1,
                                    backgroundColor: "white",
                                    borderBottom: "1px solid #e0e0e0",
                                    flexShrink: 0,
                                },
                                "& .MuiDataGrid-row": {
                                    cursor: "default",
                                },
                                "& .MuiDataGrid-cell": {
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    textAlign: "center",
                                },
                                "& .MuiDataGrid-columnHeader": {
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                },
                                "& .MuiDataGrid-columnHeaderTitleContainer": {
                                    justifyContent: "center",
                                },
                                "& .MuiDataGrid-columnHeaderTitle": {
                                    width: "100%",
                                    textAlign: "center",
                                },
                                "& .MuiDataGrid-cellContent": {
                                    width: "100%",
                                    textAlign: "center",
                                },
                                "& .MuiDataGrid-row:hover": {
                                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                                },
                            }}
                        />
                    </div>
                </div>

                {customers.length > 0 && (
                    <Pagination
                        currentPage={page}
                        totalPages={String(totalPages)}
                        onPageChange={setPage}
                    />
                )}
            </div>
        </Sidebar>
    );
}

export default CreditUnblockDateEditor;